/**
 * Vision exercise type definitions
 *
 * The vision exercise is a 5-step interactive journey:
 * 1. Vision Map - drag pins onto life zones
 * 2. Dream Catcher - tap floating bubbles to catch dreams
 * 3. Vision Slots - spin reels and rate the combos
 * 4. Headlines - pick newspaper headlines about your future
 * 5. One Wish - single text input for your deepest wish
 */

// Slot result with rating
export interface SlotResult {
  combo: string // e.g., "You finally embrace adventure"
  rating: 'fire' | 'meh' | 'nope'
}

// Complete vision exercise data (new interactive format)
export interface VisionData {
  // Step 1: Map pins - which zones user focused on
  focusAreas: string[] // 1-3 zone IDs

  // Step 2: Dream catcher - dreams user caught
  caughtDreams: string[] // 3-5 dreams

  // Step 3: Slots - spin results with ratings
  slotResults: SlotResult[] // 3 results

  // Step 4: Headlines - selected newspaper headlines
  chosenHeadlines: string[] // 2 headlines

  // Step 5: One wish - free text
  oneWish: string

  // Optional: Additional context about their vision
  additionalContext?: string
}

// AI-generated Vision Synthesis (for wrapped)
export interface VisionSynthesis {
  futureNarrative: string // Cohesive story of their future year
  keyThemes: string[] // 3-4 themes from their vision
  glimpse: string // One sentence future glimpse
}

/**
 * @deprecated Legacy types from v1.0 for backward compatibility.
 * New users should use VisionData with the interactive format.
 */
export interface LegacyVisionResponse {
  promptId: string
  response: string
}

/**
 * @deprecated Legacy format from v1.0 (text-based responses).
 * Kept for backward compatibility with existing user data.
 * New users should use VisionData.
 */
export interface LegacyVisionData {
  responses: LegacyVisionResponse[]
  extractedGoals: string[]
}

// Helper to check if vision data is in new format
export function isNewVisionFormat(data: VisionData | LegacyVisionData | null): data is VisionData {
  if (!data) return false
  return 'focusAreas' in data || 'caughtDreams' in data || 'oneWish' in data
}

// Helper to create empty vision data
export function createEmptyVisionData(): VisionData {
  return {
    focusAreas: [],
    caughtDreams: [],
    slotResults: [],
    chosenHeadlines: [],
    oneWish: '',
    additionalContext: '',
  }
}
